import { useState } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';

/**
 * Shimmer loading skeleton — matches dark theme.
 * Use: <Skeleton className="h-4 w-32" /> or <Skeleton className="h-48" />
 */
export function Skeleton({ className = '' }) {
  return (
    <div
      className={`animate-pulse bg-white/[0.06] rounded ${className}`}
    />
  );
}

/** Card-shaped skeleton for dashboard KPI cards */
export function SkeletonCard() {
  return (
    <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-8 w-8 rounded-lg" />
      </div>
      <Skeleton className="h-7 w-28 mb-2" />
      <Skeleton className="h-3 w-16" />
    </div>
  );
}

/** Table row skeleton */
export function SkeletonRow({ cols = 6 }) {
  return (
    <div className="flex items-center gap-4 px-4 py-3 border-b border-white/[0.04]">
      {Array.from({ length: cols }).map((_, i) => (
        <Skeleton key={i} className="h-4 flex-1" />
      ))}
    </div>
  );
}

/** Full-section loading skeleton (cards + table) */
export function DashboardSkeleton({ cards = 4, rows = 6, cols = 5 }) {
  return (
    <div className="animate-in fade-in duration-300" data-testid="loading-skeleton">
      <div className="flex items-center gap-2 mb-6">
        <Skeleton className="h-7 w-48" />
      </div>
      <div className={`grid grid-cols-${cards} gap-4 mb-6`}>
        {Array.from({ length: cards }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-white/[0.06]">
          <Skeleton className="h-4 w-32" />
        </div>
        {Array.from({ length: rows }).map((_, i) => (
          <SkeletonRow key={i} cols={cols} />
        ))}
      </div>
    </div>
  );
}

/** Error banner with retry button */
export function ErrorBanner({ message, onRetry }) {
  const [retrying, setRetrying] = useState(false);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await onRetry?.();
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div
      className="flex items-center gap-3 bg-red-500/10 border border-red-500/20 rounded-xl px-5 py-4 mb-6"
      data-testid="error-banner"
    >
      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
      <p className="text-sm text-red-300 flex-1">
        {message || 'Failed to load data. Please try again.'}
      </p>
      {onRetry && (
        <button
          onClick={handleRetry}
          disabled={retrying}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-300 border border-red-500/30 rounded-lg hover:bg-red-500/10 transition-colors disabled:opacity-50"
          data-testid="retry-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${retrying ? 'animate-spin' : ''}`} />
          {retrying ? 'Retrying...' : 'Retry'}
        </button>
      )}
    </div>
  );
}
